import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useRef } from "react";
import styled from "styled-components";
import JobStatusTag from "./JobStatusTag";
import Divider from "./library/Divider";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerOverlay,
} from "./library/Drawer";
import Loader from "./library/Loader";
import TextLink from "./library/TextLink";
import { humanizeSalaryRange, humanizeTimeAgo } from "../helpers/formatters";
import { useBoolean } from "../hooks";
import { useJobPreview } from "../api/jobs";
import JobEngagementForm from "./JobEngagementForm";
import { capitalize } from "lodash";
import ScrollDownButton from "./ScrollDownButton";

type RecruiterJobPreviewDrawerProps = {
  isOpen: boolean;
  jobId: string;
  onClose: () => void;
  onEngageSuccess: () => void;
};

const RecruiterJobPreviewDrawer = (props: RecruiterJobPreviewDrawerProps) => {
  const { jobId, isOpen, onClose, onEngageSuccess } = props;
  const [moreStatsVisible, setMoreStatsVisible] = useBoolean();
  const engagementForm = useRef<HTMLDivElement>();

  const { data: job, isLoading } = useJobPreview(jobId);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} id="job-preview-drawer">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerClose />
        {isLoading && <Loader />}
        {job && (
          <>
            <RecruiterDrawerHeader>
              <JobTitle>{job.title}</JobTitle>
              <InitialDetails>
                <div>
                  <Salary>
                    {humanizeSalaryRange(
                      job.salaryMin,
                      job.salaryMax,
                      job.currency?.isoCode
                    )}
                  </Salary>
                  <MutedText>Listed {humanizeTimeAgo(job.listedAt)}</MutedText>
                </div>
                <JobStatusTag status={job.status} />
              </InitialDetails>
            </RecruiterDrawerHeader>

            <DataItem>
              <DataLabel>Placement Fee</DataLabel>
              <DataValue>{job.placementFeePercent}%</DataValue>
            </DataItem>

            <DataItem hidden={!job.sector}>
              <DataLabel>Sector</DataLabel>
              <DataValue>{job.sector?.name}</DataValue>
            </DataItem>

            <DataItem>
              <DataLabel>Vacancy reason</DataLabel>
              <DataValue>{job.vacancyReason}</DataValue>
            </DataItem>

            <DataItem>
              <DataLabel>Key skills</DataLabel>
              <DataValue>
                {job.keySkills.split(",").map((skill, index, skills) => (
                  <span key={skill}>
                    {skill}
                    {index < skills.length - 1 && ","}
                  </span>
                ))}
              </DataValue>
            </DataItem>

            <DataItem>
              <DataLabel>Type</DataLabel>
              <DataValue>{capitalize(job.jobType)}</DataValue>
            </DataItem>

            <DataItem>
              <DataLabel>Interview stages</DataLabel>
              <DataValue>{job.interviewStages}</DataValue>
            </DataItem>

            <DataItem>
              <DataLabel>Positions</DataLabel>
              <DataValue>
                {job.numberOfPositions}{" "}
                {job.filledPositionsCount > 0 &&
                  `(${job.filledPositionsCount} filled)`}
              </DataValue>
            </DataItem>

            <DataItem hidden={!job.keywords}>
              <DataLabel>Keywords</DataLabel>
              <DataValue>{job.keywords}</DataValue>
            </DataItem>

            <DataItem>
              <DataLabel>Account manager</DataLabel>
              <DataValue>{job.employer.accountManager}</DataValue>
            </DataItem>

            <DataItem
              hidden={!job.employer.customTerms && !job.employer.customTermsUrl}
            >
              <DataLabel>Employer t&c&apos;s</DataLabel>
              <DataValue>
                {job.employer.customTermsUrl ? (
                  <TextLink href={job.employer.customTermsUrl}>
                    View terms
                  </TextLink>
                ) : (
                  job.employer.customTerms
                )}
              </DataValue>
            </DataItem>

            <DataItem hidden={!job.degreeRequired}>
              <DataLabel>Degree required</DataLabel>
              <DataValue>✅</DataValue>
            </DataItem>

            <DataItem hidden={!job.relocationAssistance}>
              <DataLabel>Relocation assistance</DataLabel>
              <DataValue>✅</DataValue>
            </DataItem>

            <DataItem hidden={!job.visaSponsorship}>
              <DataLabel>Visa sponsership</DataLabel>
              <DataValue>✅</DataValue>
            </DataItem>

            <DataItem>
              <DataLabel>Employer Avg Response</DataLabel>
              <DataValue>
                {job.employer.averageTimeToRespond || "New account"}
              </DataValue>
            </DataItem>

            <ClickableDataItem
              id={moreStatsVisible ? "hide-job-stats" : "show-job-stats"}
              onClick={setMoreStatsVisible.toggle}
            >
              <DataLabel>
                <FontAwesomeIcon
                  icon={moreStatsVisible ? faChevronDown : faChevronRight}
                />{" "}
                {`${moreStatsVisible ? "Hide" : "Show"} application status`}
              </DataLabel>
            </ClickableDataItem>

            {moreStatsVisible && (
              <Sublist>
                <DataItem>
                  <DataLabel>Total applications</DataLabel>
                  <DataValue>{job.applicationsCount}</DataValue>
                </DataItem>

                <DataItem>
                  <DataLabel>Rejected</DataLabel>
                  <DataValue>
                    {job.applicationStats.candidates_rejected}
                  </DataValue>
                </DataItem>

                <DataItem>
                  <DataLabel>Interviewing</DataLabel>
                  <DataValue>
                    {job.applicationStats.candidates_at_interview}
                  </DataValue>
                </DataItem>

                <DataItem>
                  <DataLabel>Offers</DataLabel>
                  <DataValue>{job.applicationStats.offers_made}</DataValue>
                </DataItem>
              </Sublist>
            )}

            <ScrollDownButton to={engagementForm} />

            <Divider />

            <div ref={engagementForm}>
              <JobEngagementForm
                jobId={jobId}
                employerId={job.employer.id}
                hasCustomTerms={Boolean(
                  job.employer.customTerms || job.employer.customTermsUrl
                )}
                onSuccess={onEngageSuccess}
              />
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};

const DataItem = styled.div<{ hidden?: boolean }>`
  display: ${({ hidden }) => (hidden ? "none" : "flex")};
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.gray100};
  line-height: 1.25;
`;

const DataLabel = styled.div`
  margin-right: 1rem;
  min-width: 100px;
`;

const ClickableDataItem = styled(DataItem)`
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray100};
  }
`;

const InitialDetails = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

const DataValue = styled.div`
  font-weight: bold;
  text-align: right;
`;

const Sublist = styled.div`
  padding-left: 2rem;
`;

const MutedText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.muted};
`;

const JobTitle = styled.div`
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 1.5rem;
  line-height: 1.25;
`;

const Salary = styled.div`
  margin-bottom: 0.25rem;
`;

const RecruiterDrawerHeader = styled.div`
  margin-bottom: 2rem;
`;

export default RecruiterJobPreviewDrawer;
